/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { ListOrdered } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Grid,
	GridItem,
	Text
} from '@chakra-ui/react';
import { useKeystoneSession } from '../../../auth/use-keystone-session';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Blueprint } from '@rta/rta-blueprint-library';
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Kitchen3dController as TdComponentController } from '@rta/rta-blueprint-3d-component';

import { Designs } from './designs.type';
import { DesignTabSelection } from './design-tab-selection';
import { DesignViewSelection } from './design-view-selection';
import { FavoriteConfirmationModal, ManageDesignsModal } from '../../modals';
import { RenderingCaptureBox, RtaFeatureIconButton, RtaIconButton } from '@components';
import { useAddSubmissionFeature } from '@features';
import { AppContext, DesignActionTypes, useDeleteDesign, useLoadComponentData, useLoadMoreDesigns } from '@context';
import { patchBlueprintMetadata, updateDealDesignImages } from '@external';
import { getDesignImage, isRenderingStale } from '@helpers';
import { lang } from '@lang';
import { projectState, selectNavigation, useAppSelector } from '@state';
import { useAppToast, useDesign } from '@hooks';

export const DesignCollectionControls = () => {
	const { appState: { customerDesigns }} = useContext(AppContext);
	const { appDispatch } = useContext(AppContext);
	const [controller] = useDesign<BpController | TdComponentController>();
	const navigate = useNavigate();
	const appNav = useAppSelector(selectNavigation);
	const deleteBp = useDeleteDesign({ appNav });
	const componentData = useLoadComponentData();
	const { hasMore, remaining, isLoadingMore, loadMore } = useLoadMoreDesigns();
	// a design that failed to hydrate would otherwise leave a Load More that has no page left to fetch
	const unloadedCount = hasMore ? remaining : 0;

	const { getToken } = useKeystoneSession();
	const addNewSubmission = useAddSubmissionFeature();
	const project = useAppSelector(projectState);
	const appToast = useAppToast();

	const [manageModalOpen, setManageModalOpen] = useState(false);

	// Favorite confirmation modal state
	const [favoriteModalOpen, setFavoriteModalOpen] = useState(false);
	const [favoriteModalDesignId, setFavoriteModalDesignId] = useState<string | null>(null);
	const [favoriteModalDesignName, setFavoriteModalDesignName] = useState('');
	const [favoriteModalDeal, setFavoriteModalDeal] = useState<typeof project[number] | null>(null);
	const [favoriteModalBpController, setFavoriteModalBpController] = useState<BpController | null>(null);
	const [favoriteModalTdController, setFavoriteModalTdController] = useState<TdComponentController | null>(null);
	const [favoriteModalRenderingUrl, setFavoriteModalRenderingUrl] = useState('');

	// Favorite design — persisted in blueprint metadata
	const [favoriteId, setFavoriteId] = useState<string | null>(() => {
		const favDesign = customerDesigns.designs.find((d) => d.blueprint.metadata.favorite);
		return favDesign?.blueprint.id ?? null;
	});

	// Rendering staleness guard state
	const [staleGuardDesignId, setStaleGuardDesignId] = useState<string | null>(null);
	const staleGuardCancelRef = useRef<HTMLButtonElement>(null);
	const staleGuardDesign = customerDesigns.designs.find((d) => d.blueprint.id === staleGuardDesignId) ?? null;

	// Sync favoriteId when designs load
	useEffect(() => {
		const favDesign = customerDesigns.designs.find((d) => d.blueprint.metadata.favorite);
		setFavoriteId(favDesign?.blueprint.id ?? null);
	}, [customerDesigns.designs]);

	const executeFavorite = useCallback((designId: string) => {
		if (!appNav.email) return;
		const { email } = appNav;
		const isFavoriting = favoriteId !== designId;

		// Clear old favorite in-memory only (backend handles unfavoriting other designs)
		if (isFavoriting && favoriteId) {
			const oldFavorite = customerDesigns.designs.find((d) => d.blueprint.id === favoriteId);
			if (oldFavorite) {
				oldFavorite.blueprint.metadata.favorite = false;
			}
		}

		// Update in-memory metadata
		const target = customerDesigns.designs.find((d) => d.blueprint.id === designId);
		if (target) target.blueprint.metadata.favorite = isFavoriting;

		// Update local state for immediate UI feedback
		setFavoriteId(isFavoriting ? designId : null);

		// Single API call — backend unfavorites other designs when favorite=true
		const submissionId = target?.blueprint.submissionId ?? designId;
		patchBlueprintMetadata({ customerEmail: email, designId: submissionId, metadata: { favorite: isFavoriting }, getToken, mode: 'live' });
	}, [customerDesigns.designs, favoriteId, appNav.email, getToken]);

	const handleFavorite = useCallback((designId: string) => {
		if (!appNav.email) return;
		const isFavoriting = favoriteId !== designId;

		// Unfavoriting — just do it directly, no modal needed
		if (!isFavoriting) {
			executeFavorite(designId);
			return;
		}

		// Favoriting — check for stale rendering before proceeding
		const design = customerDesigns.designs.find((d) => d.blueprint.id === designId);
		if (!design) return;

		const kitchen = design.bpController?.kitchen ?? design.tdController?.kitchen ?? null;

		// Block if the design was mutated after the rendering was captured, or no image exists yet
		if (isRenderingStale({ design })) {
			setStaleGuardDesignId(designId);
			return;
		}

		const dealId = kitchen?.dealId ?? '';
		const deal = project.find((d) => d.dealId === dealId) ?? project?.[0] ?? null;

		// No deal association — favorite without modal
		if (!deal || !deal.dealId) {
			executeFavorite(designId);
			return;
		}

		// Show confirmation modal — 3D preview uses the saved rendering, no live 3D render
		setFavoriteModalDesignId(designId);
		setFavoriteModalDesignName(kitchen?.description ?? design.blueprint.kitchens.getActiveVersion()?.description ?? 'Untitled');
		setFavoriteModalDeal(deal);
		setFavoriteModalBpController(design.bpController);
		setFavoriteModalTdController(design.tdController);
		setFavoriteModalRenderingUrl(getDesignImage(design.blueprint));
		setFavoriteModalOpen(true);
	}, [customerDesigns.designs, favoriteId, appNav.email, project, executeFavorite]);

	const handleStaleGuardProceed = useCallback((designId: string) => {
		setStaleGuardDesignId(null);
		const design = customerDesigns.designs.find((d) => d.blueprint.id === designId);
		if (!design) return;
		const kitchen = design.bpController?.kitchen ?? design.tdController?.kitchen ?? null;
		const dealId = kitchen?.dealId ?? '';
		const deal = project.find((d) => d.dealId === dealId) ?? project?.[0] ?? null;

		if (!deal || !deal.dealId) {
			executeFavorite(designId);
			return;
		}

		setFavoriteModalDesignId(designId);
		setFavoriteModalDesignName(kitchen?.description ?? design.blueprint.kitchens.getActiveVersion()?.description ?? 'Untitled');
		setFavoriteModalDeal(deal);
		setFavoriteModalBpController(design.bpController);
		setFavoriteModalTdController(design.tdController);
		setFavoriteModalRenderingUrl(getDesignImage(kitchen?.context?.blueprint));
		setFavoriteModalOpen(true);
	}, [customerDesigns.designs, project, executeFavorite]);

	const handleFavoriteModalConfirm = useCallback(async (overheadImage: string, threeDImage: string) => {
		if (!favoriteModalDesignId || !appNav.email || !favoriteModalDeal) return;

		// Execute the favorite (metadata patch — fire and forget)
		executeFavorite(favoriteModalDesignId);

		// The modal already resolves saved-vs-live and strips the data-URL prefix so threeDImage is
		// raw base64 the backend can Buffer.from(_, 'base64'). Re-reading getDesignImage here would
		// override that with the full data URL, whose "data:image/jpeg;base64," prefix decodes to
		// garbage bytes and produces a broken .png on the HubSpot deal.
		const [_success, error] = await updateDealDesignImages({
			dealId: favoriteModalDeal.dealId,
			email: appNav.email,
			designLabel: favoriteModalDesignName,
			overheadImage,
			threeDImage,
			getToken
		});

		setFavoriteModalOpen(false);

		if (error) {
			appToast.error({ title: 'Failed to upload design images', description: error });
		} else {
			appToast.success({ title: 'Design images sent to HubSpot' });
		}
	}, [favoriteModalDesignId, favoriteModalDeal, appNav.email, getToken, executeFavorite, appToast, customerDesigns.designs]);

	const submissions: Designs = useMemo(() => {
		const mapped = customerDesigns.designs.map((bpContextRecord) => {
			const bp = bpContextRecord.blueprint;
			const kitchen = bp.kitchens.getActiveVersion();
			const { submissionDate } = bpContextRecord.blueprint;
			const readOnly = !!(bpContextRecord.bpController?.readonly ?? bpContextRecord.tdController?.readonly);
			const image = getDesignImage(bp);
			// New inline thumbnail/canonical rendering is a data URL or http(s) URL already; legacy
			// bare thumbnail is a bare base64 (>255) or a filename needing the images base URL.
			const thumbnailSrc = !image
				? ''
				: image.startsWith('data:') || /^https?:\/\//i.test(image)
					? image
					: image.length > 255
						? `data:image/png;base64,${image}`
						: `${import.meta.env.VITE_BLUEPRINT_IMAGES_BASE_URL}\\${image}`;
			return {
				id: bp.id,
				version: kitchen?.version ?? null,
				baseVersion: kitchen?.baseVersion ?? null,
				description: kitchen?.description ?? '',
				thumbnail: thumbnailSrc,
				isDirty: bpContextRecord.isDirty,
				readOnly,
				enabled: !controller?.readonly,
				submissionDate,
				submissionId: bp.submissionId,
				priority: bp.metadata.priority ?? 0
			};
		});
		// mirrors the server's order: manually pinned designs first, then newest submission first
		mapped.sort((a, b) => (a.priority || Infinity) - (b.priority || Infinity) || Number(b.submissionDate) - Number(a.submissionDate));
		return mapped;
	}, [customerDesigns.designs, controller?.readonly]);

	const idsKey = useMemo(() => submissions.map((d) => d.id).join('|'), [submissions]);
	const selectedId = useMemo(() => submissions.some((d) => d.id === appNav.designId) ? appNav.designId : null, [submissions, appNav.designId]);

	// Auto-load favorite design when no specific design is selected
	useEffect(() => {
		if (!favoriteId || !appNav.email || selectedId || submissions.length === 0) return;
		const favoriteDesign = submissions.find((d) => d.id === favoriteId);
		if (!favoriteDesign) return;
		const { id, version, baseVersion } = favoriteDesign;
		navigate(`/r/${appNav.email}/${id}${version ? `/${version}` : `/${baseVersion ?? ''}?draft`}`);
	}, [favoriteId, appNav.email, selectedId, submissions, navigate]);

	// disable design controls if no designs are available
	const disableControls = !customerDesigns || customerDesigns.designs.length === 0;

	// Always show tabs — they scroll horizontally
	const selectionTabColumn = '1fr';
	const templateColumns = `30px 30px 30px ${selectionTabColumn}`;

	if (!componentData) return null;
	// Selection Tabs JSX
	const selectionTabs = <GridItem minWidth="0" maxHeight="36px" overflow="hidden">
			<Box overflow="auto">
				<DesignTabSelection
					key={`${selectedId ?? 'none'}:${idsKey}`}
					designs={submissions}
					selected={selectedId}
					disabled={disableControls}
					favoriteId={favoriteId}
					remaining={unloadedCount}
					isLoadingMore={isLoadingMore}
					onLoadMore={loadMore}
					onSelect={(id, version, baseVersion) => {
						navigate(`/r/${appNav.email}/${id}${version ? `/${version ?? ''}` : `/${baseVersion ?? ''}?draft`}`);
					}}
					onDesignNameChange={
						(designId, newName) => {
						// console.log('onDesignNameChange', designId, newName, appNav.designId);
							const bp = customerDesigns.designs.find((design) => design.blueprint.id === designId)?.blueprint;
							if (bp) {
								const activeVersion = bp.kitchens.getActiveVersion();
								if (activeVersion) activeVersion.description = newName;
							}
						}
					}
					onDelete={
						async (designId) => {
							// wait for the server delete to actually land before dropping it from local state -- otherwise a reload before the
							// request finishes re-hydrates the design from metadata.json and it looks like the delete never happened.
							const success = await deleteBp(designId);
							if (!success) {
								appToast.error({ title: lang.customer.message.deleteCustomerBlueprintFailed });
								return;
							}
							appDispatch({ type: DesignActionTypes.deleteBlueprint, payload: designId });
						}
					}
					onDuplicate={(designId) => {
						const design = customerDesigns.designs.find((design) => design.blueprint.id === designId);
						const bp = design?.bpController?.blueprint;
						if (!design || !bp || !design.bpController) return;

						const newId = '';
						const bpRaw = bp.serialize();
						const bpKitchen = design.bpController.kitchen.serialize();
						if (bpKitchen) bpKitchen.baseVersion = null;
						bpRaw.kitchens.children = [bpKitchen];
						const newBp = new Blueprint({ dataIn: { ...bpRaw, id: newId, submissionId: null }, userEmail: bp?.email });
						newBp.kitchens.children.forEach((kitchen) => {
							kitchen.description = `${kitchen.description} - Copy`;
						});
						if (bp) {
							appDispatch({ type: DesignActionTypes.addBlueprint, payload: { blueprint: newBp, version: null, useDraft: true, isDirty: false }});
							appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: newBp.id, isDirty: true }});
							navigate(`/r/${appNav.email}/${newBp.id}/1?draft`);
						}
					}}
					onFavorite={handleFavorite}/>
			</Box>
		</GridItem>;

	return (
		<Box w={'100%'} h={'100%'} m={'1px'}>
			<Grid templateColumns={templateColumns} w={'100%'} h={'100%'} gap={'5px'} ml="5px">
				<GridItem mt="2px" position={'relative'}>
					<RtaIconButton
						icon={<ListOrdered/>}
						enabled={!disableControls}
						label={lang.customer.tooltip.manageDesigns}
						onClick={() => setManageModalOpen(true)}/>
				</GridItem>
				<GridItem mt="2px" position={'relative'}>
				<DesignViewSelection
						designs={submissions}
					selected={selectedId}
					disabled={disableControls}
						remaining={unloadedCount}
						isLoadingMore={isLoadingMore}
						onLoadMore={loadMore}
						onSelect={(id, version, baseVersion) => {
							navigate(`/r/${appNav.email}/${id}${version ? `/${version ?? ''}` : `/${baseVersion ?? ''}?draft`}`);
						}}/>
				</GridItem>
				<GridItem mt="2px" position={'relative'}>
					<RtaFeatureIconButton feature={addNewSubmission} />
				</GridItem>
				{selectionTabs}
			</Grid>
			<ManageDesignsModal isOpen={manageModalOpen} onClose={() => setManageModalOpen(false)} />
			<FavoriteConfirmationModal
				isOpen={favoriteModalOpen}
				onClose={() => setFavoriteModalOpen(false)}
				onConfirm={handleFavoriteModalConfirm}
				designName={favoriteModalDesignName}
				deal={favoriteModalDeal}
				bpController={favoriteModalBpController}
				tdController={favoriteModalTdController}
				savedRenderingUrl={favoriteModalRenderingUrl}
			/>
			<AlertDialog
				isOpen={staleGuardDesignId !== null}
				leastDestructiveRef={staleGuardCancelRef}
				onClose={() => setStaleGuardDesignId(null)}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Rendering may be out of date
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text fontSize="sm">
								This design changed after its rendering was captured, or has no rendering set. The image sent to HubSpot may not match the current kitchen.
								Capture a fresh rendering below, or continue anyway.
							</Text>
							{staleGuardDesign?.tdController && (
								<Box mt={3}>
									<RenderingCaptureBox
										tdController={staleGuardDesign.tdController}
										active={staleGuardDesignId !== null}
									/>
								</Box>
							)}
						</AlertDialogBody>
						<AlertDialogFooter gap={2}>
							<Button
								ref={staleGuardCancelRef}
								onClick={() => setStaleGuardDesignId(null)}
							>
								Cancel
							</Button>
							<Button
								colorScheme="blue"
								onClick={() => staleGuardDesignId && handleStaleGuardProceed(staleGuardDesignId)}
							>
								Continue Anyway
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Box>
	);
};
